import { hasCognitoSession } from "./auth-service-client";
import { getCookie } from "../cookieHelper";
import { isIwocaDE } from "../countryHelper";
import { getRuntimeEnvironment } from "../utils/getRuntimeEnvironment";

export type UserType =
  | "customer"
  | "introducer"
  | "staff"
  | "partner_developer"
  | "introducer_customer"
  | "seller_access_user"
  | "personal_guarantor_access_user"
  | null;
export type ProfileResponse = {
  data: {
    user_id: number;
    user_type: UserType;
  };
};
type Args = {
  redirectUrl?: string;
  isTestingLoggedIn?: boolean;
  tokens?: {
    idToken: string;
    accessToken: string;
    refreshToken?: string;
  };
};

export const REDIRECT_INFO_LOG_MESSAGE = "Redirecting logged in user";
export const UNEXPECTED_REDIRECT_ERROR_LOG_MESSAGE =
  "Unexpected error while redirecting";
export const UNEXPECTED_CUSTOMER_PROFILE_ERROR_LOG_MESSAGE =
  "Unexpected customer profile for login";

/**
 * Redirect user after successful authentication.
 *
 * Staged rollout of the D5 cutover (core-systems/projects#263):
 *
 * - **Staging** — Django-session success skips the `/profile/` fetch and
 *   navigates straight to `redirectUrl || "/home"`. Destination routing
 *   lives in iwoca-home-frontend's useMe + destination router
 *   (iwoca-home-frontend!20). A full-page nav escapes the WebKit
 *   cookie-visibility gap (bug 255524) that was dropping ~8k customers/day
 *   onto Webflow marketing.
 * - **Production** — legacy behaviour retained until the iwoca-home prod
 *   build flips `VITE_ENABLE_DJANGO_SESSION_FALLBACK` to true. The
 *   `/profile/` fetch stays so user_type dispatch (introducer, staff,
 *   partner_developer, DE partner) keeps working. The `/`→`/my_account`
 *   fallthrough shipped in !280 remains in place for the WebKit-race case.
 *
 * Cognito hosted-UI success path is env-independent.
 */
export const redirectUser = ({
  redirectUrl,
  isTestingLoggedIn = false,
  tokens,
}: Args = {}): Promise<void> => {
  if (tokens) {
    const targetUrl = redirectUrl || "/home";
    const tokenFragment = `#idToken=${encodeURIComponent(tokens.idToken)}&accessToken=${encodeURIComponent(tokens.accessToken)}${tokens.refreshToken ? `&refreshToken=${encodeURIComponent(tokens.refreshToken)}` : ""}`;
    window.location.href = targetUrl + tokenFragment;
    return Promise.resolve();
  }

  if (getRuntimeEnvironment() === "staging") {
    return redirectUserStaging({ redirectUrl, isTestingLoggedIn });
  }

  return redirectUserProduction({ redirectUrl, isTestingLoggedIn });
};

// Staging: no /profile/ fetch, no user_type dispatch — iwoca-home owns
// identity resolution once the browser navigates to /home.
const redirectUserStaging = ({
  redirectUrl,
  isTestingLoggedIn,
}: {
  redirectUrl?: string;
  isTestingLoggedIn: boolean;
}): Promise<void> => {
  if (isTestingLoggedIn && !redirectUrl) {
    return Promise.reject(
      new Error("redirectUser: session-check no longer supported in staging"),
    );
  }

  window.location.href = redirectUrl || "/home";
  return Promise.resolve();
};

// Production: verbatim master behaviour. Retire once iwoca-home flips
// VITE_ENABLE_DJANGO_SESSION_FALLBACK=true in the prod build.
const redirectUserProduction = async ({
  redirectUrl,
  isTestingLoggedIn,
}: {
  redirectUrl?: string;
  isTestingLoggedIn: boolean;
}): Promise<void> => {
  if (redirectUrl && !isTestingLoggedIn) {
    window.location.href = redirectUrl;
    return;
  }

  const response = await fetch("/api/lending/edge/profile/", {
    method: "GET",
    credentials: "same-origin",
    headers: {
      "X-CSRFToken": getCookie("csrftoken") as string,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  if (isTestingLoggedIn && redirectUrl && response.ok) {
    window.location.href = redirectUrl;
    return;
  }

  if (!response.ok && isTestingLoggedIn) {
    throw response.status;
  }

  try {
    const payload: ProfileResponse = await response.json();
    if (
      payload.data.user_type === "customer" ||
      payload.data.user_type === "introducer_customer" ||
      payload.data.user_type === "seller_access_user" ||
      payload.data.user_type === "personal_guarantor_access_user"
    ) {
      if (await hasCognitoSession()) {
        window.location.href = "/home";
      } else {
        window.location.href = "/my_account";
      }
      return;
    }

    if (payload.data.user_type === "introducer") {
      if (isIwocaDE()) {
        window.location.href = "/partner/account";
        return;
      }

      window.location.href = "/introducer/account";
      return;
    }

    if (payload.data.user_type === "partner_developer") {
      window.location.href = "/developer/portal";
      return;
    }

    if (payload.data.user_type === "staff") {
      window.location.href = "/backyard";
      return;
    }
  } catch (error) {
    if (!response.ok && isTestingLoggedIn) {
      throw error;
    }
  }

  window.location.href = "/my_account";
};
